import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Alert,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DatePicker2 from "../../components/DatePicker2";
import TerritorySelect from "../../components/AsyncSelects/TerritorySelect";
import { getSalesAnalytics } from "../../distflowAPI/salesAnalyticsApi";
import useAppSelector from "../../hooks/useAppSelector";
import SalesCharts from "./SalesCharts";
import { previousPeriodComparison, resolveDateRange } from "./dateRanges";
import "./SalesAnalytics.css";

dayjs.extend(utc);

const DIMENSIONS = [
  {
    key: "route",
    title: "By route",
    icon: <RouteOutlinedIcon fontSize="small" />,
    description: "Where deliveries are selling",
  },
  {
    key: "customer",
    title: "By customer",
    icon: <PeopleOutlineIcon fontSize="small" />,
    description: "Your highest value customers",
  },
  {
    key: "vendor",
    title: "By vendor",
    icon: <StorefrontOutlinedIcon fontSize="small" />,
    description: "Sales across your suppliers",
  },
  {
    key: "product_category",
    title: "By product category",
    icon: <CategoryOutlinedIcon fontSize="small" />,
    description: "Which products lead growth",
  },
  {
    key: "account_type",
    title: "By account type",
    icon: <ApartmentOutlinedIcon fontSize="small" />,
    description: "Performance by account group",
  },
  {
    key: "salesman",
    title: "By salesmen / office",
    icon: <BadgeOutlinedIcon fontSize="small" />,
    description: "Sales by team attribution",
  },
];

const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week to date" },
  { value: "last_week", label: "Last full week" },
  { value: "mtd", label: "Month to date" },
  { value: "last_month", label: "Last full month" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom dates" },
];

const COMPARISONS = [
  { value: "previous_period", label: "Previous period" },
  { value: "previous_month", label: "Same period last month" },
  { value: "previous_hebrew_year", label: "Same period last Hebrew year" },
  { value: "custom", label: "Custom dates" },
  { value: "none", label: "No comparison" },
];

const moneyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const countFormat = new Intl.NumberFormat("en-US");

function money(value) {
  return moneyFormat.format(Number(value || 0));
}

function share(value) {
  return value === null || value === undefined
    ? "—"
    : `${Number(value).toFixed(2)}%`;
}

function dateLabel(date) {
  return date ? dayjs.utc(date).format("MMM D, YYYY") : "—";
}

function rangeLabel(range) {
  if (!range) return "";
  return `${dateLabel(range.start_date)} – ${dateLabel(range.end_date)}`;
}

function comparisonLabel(mode) {
  return COMPARISONS.find((item) => item.value === mode)?.label || "Comparison";
}

function PercentChange({ value, compact = false }) {
  if (value === undefined) return null;
  const number = value === null ? null : Number(value);
  const direction =
    number === null ? "new" : number > 0 ? "up" : number < 0 ? "down" : "flat";
  return (
    <span
      className={`sales-analytics-change sales-analytics-change--${direction}`}
    >
      {number === null
        ? "No baseline"
        : `${number > 0 ? "+" : ""}${number.toFixed(2)}%`}
      {!compact && (
        <span className="sales-analytics-change-caption"> vs. comparison</span>
      )}
    </span>
  );
}

function MetricCard({ label, value, change, featured = false, note }) {
  return (
    <div
      className={`sales-analytics-metric ${featured ? "sales-analytics-metric--featured" : ""}`}
    >
      <div className="sales-analytics-metric-label">{label}</div>
      <div className="sales-analytics-metric-value">{value}</div>
      <div className="sales-analytics-metric-bottom">
        {change !== undefined ? (
          <PercentChange value={change} />
        ) : (
          <span>{note || "Selected period"}</span>
        )}
      </div>
    </div>
  );
}

function BreakdownCard({ dimension, rows, onOpen, loading }) {
  const preview = rows.slice(0, 5);
  return (
    <section
      className="sales-analytics-breakdown-card"
      role="button"
      tabIndex={loading ? -1 : 0}
      aria-disabled={loading}
      aria-label={`View ${dimension.key.replaceAll("_", " ")} breakdown`}
      onClick={loading ? undefined : onOpen}
      onKeyDown={(event) => {
        if (!loading && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="sales-analytics-breakdown-heading">
        <span className="sales-analytics-dimension-icon" aria-hidden="true">
          {dimension.icon}
        </span>
        <div>
          <h3>{dimension.title}</h3>
          <p>{dimension.description}</p>
        </div>
        <span className="sales-analytics-breakdown-count">{rows.length}</span>
      </div>
      <div className="sales-analytics-preview">
        {loading ? (
          <div className="sales-analytics-preview-empty">
            Loading breakdown…
          </div>
        ) : preview.length ? (
          preview.map((row, index) => (
            <div
              className="sales-analytics-preview-row"
              key={row.key ?? `${row.id ?? row.name}-${index}`}
            >
              <span className="sales-analytics-preview-rank">{index + 1}</span>
              <span className="sales-analytics-preview-name" title={row.name}>
                {row.name || "Unassigned"}
              </span>
              <strong>{money(row.sales)}</strong>
            </div>
          ))
        ) : (
          <div className="sales-analytics-preview-empty">
            No sales in this period
          </div>
        )}
      </div>
      <span className="sales-analytics-view-breakdown">
        View breakdown <ArrowForwardIcon fontSize="small" />
      </span>
    </section>
  );
}

function InsightCard({ eyebrow, title, value, detail, tone = "neutral" }) {
  return (
    <div className={`sales-analytics-insight sales-analytics-insight--${tone}`}>
      <span className="sales-analytics-insight-eyebrow">{eyebrow}</span>
      <strong>{title || "No data yet"}</strong>
      {value && <span className="sales-analytics-insight-value">{value}</span>}
      {detail && (
        <span className="sales-analytics-insight-detail">{detail}</span>
      )}
    </div>
  );
}

function BreakdownDrawer({ dimension, rows, report, onClose }) {
  const [query, setQuery] = useState("");
  const filteredRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term
      ? rows.filter((row) =>
          String(row.name || "")
            .toLowerCase()
            .includes(term)
        )
      : rows;
  }, [query, rows]);
  const hasComparison = Boolean(report?.comparison);

  return (
    <Drawer
      anchor="right"
      open={Boolean(dimension)}
      onClose={onClose}
      sx={{ zIndex: 1600 }}
      PaperProps={{
        className: "sales-analytics-drawer",
        role: "dialog",
        "aria-labelledby": "sales-analytics-drawer-title",
      }}
    >
      {dimension && (
        <>
          <div className="sales-analytics-drawer-header">
            <div>
              <span className="sales-analytics-eyebrow">Sales breakdown</span>
              <h2 id="sales-analytics-drawer-title">{dimension.title}</h2>
              <p>{rangeLabel(report?.date_range)}</p>
              {hasComparison && (
                <p>Compared with {rangeLabel(report.comparison)}</p>
              )}
            </div>
            <IconButton aria-label="Close breakdown" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="sales-analytics-drawer-summary">
            <span>Total sales</span>
            <strong>{money(report?.summary?.total_sales)}</strong>
            <span>
              {rows.length} {rows.length === 1 ? "entry" : "entries"}
            </span>
          </div>
          <div className="sales-analytics-drawer-search">
            <TextField
              fullWidth
              size="small"
              placeholder={`Search ${dimension.title.toLowerCase()}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              inputProps={{
                "aria-label": `Search ${dimension.title.toLowerCase()}`,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          {hasComparison && (
            <p className="sales-analytics-table-swipe-hint">
              Swipe the table to see previous sales and change →
            </p>
          )}
          <TableContainer className="sales-analytics-drawer-table-wrap">
            <Table
              size="small"
              stickyHeader
              aria-label={`${dimension.title} sales table`}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    {dimension.title
                      .replace(/^By /, "")
                      .replace(/^./, (first) => first.toUpperCase())}
                  </TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Share</TableCell>
                  {hasComparison && (
                    <TableCell align="right">Previous sales</TableCell>
                  )}
                  {hasComparison && <TableCell align="right">Change</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, index) => (
                  <TableRow key={row.key ?? `${row.id ?? row.name}-${index}`}>
                    <TableCell className="sales-analytics-drawer-name">
                      {row.name || "Unassigned"}
                    </TableCell>
                    <TableCell align="right">{money(row.sales)}</TableCell>
                    <TableCell align="right">
                      {share(row.share_percent)}
                    </TableCell>
                    {hasComparison && (
                      <TableCell align="right">
                        {money(row.previous_sales)}
                      </TableCell>
                    )}
                    {hasComparison && (
                      <TableCell align="right">
                        <span className="sales-analytics-table-change-amount">
                          {money(row.change_amount)}
                        </span>
                        <br />
                        <PercentChange value={row.change_percent} compact />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!filteredRows.length && (
                  <TableRow>
                    <TableCell
                      colSpan={hasComparison ? 5 : 3}
                      align="center"
                      className="sales-analytics-table-empty"
                    >
                      {query
                        ? "No matching entries"
                        : "No sales in this period"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="sales-analytics-drawer-footer">
            <span>Totals reflect all entries</span>
            <strong>{money(report?.summary?.total_sales)}</strong>
          </div>
        </>
      )}
    </Drawer>
  );
}

function SalesAnalytics() {
  const isStaff = useAppSelector(
    (state) => state.user.authUser?.is_staff === true
  );
  const [initialRange] = useState(() => resolveDateRange("mtd"));
  const [rangePreset, setRangePreset] = useState("mtd");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [territory, setTerritory] = useState(null);
  const [comparison, setComparison] = useState("previous_period");
  const [appliedComparison, setAppliedComparison] = useState("previous_period");
  const [comparisonStartDate, setComparisonStartDate] = useState(null);
  const [comparisonEndDate, setComparisonEndDate] = useState(null);
  const [applied, setApplied] = useState(() => ({
    start_date: initialRange.startDate.format("YYYY-MM-DD"),
    end_date: initialRange.endDate.format("YYYY-MM-DD"),
    ...previousPeriodComparison(initialRange.startDate, initialRange.endDate),
  }));
  const [validationError, setValidationError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);

  useEffect(() => {
    if (!isStaff) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setReport(null);
    setRequestError("");
    getSalesAnalytics(applied, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setReport(data);
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
        if (error?.response?.status === 403) {
          setRequestError("Only admins can view sales analytics.");
        } else {
          setRequestError(
            "Sales analytics could not be loaded. Please try again."
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [applied, isStaff]);

  function selectRangePreset(value) {
    setRangePreset(value);
    if (value !== "custom") {
      const range = resolveDateRange(value);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    const range =
      rangePreset === "custom"
        ? { startDate, endDate }
        : resolveDateRange(rangePreset);
    const selectedStart = range.startDate;
    const selectedEnd = range.endDate;
    if (!selectedStart?.isValid() || !selectedEnd?.isValid()) {
      setValidationError("Choose valid start and end dates.");
      return;
    }
    if (selectedEnd.isBefore(selectedStart, "day")) {
      setValidationError("End date must be on or after start date.");
      return;
    }
    if (
      comparison === "custom" &&
      (!comparisonStartDate?.isValid() || !comparisonEndDate?.isValid())
    ) {
      setValidationError("Choose both custom comparison dates.");
      return;
    }
    if (
      comparison === "custom" &&
      comparisonEndDate.isBefore(comparisonStartDate, "day")
    ) {
      setValidationError(
        "Comparison end date must be on or after its start date."
      );
      return;
    }
    setValidationError("");
    setSelectedDimension(null);
    setStartDate(selectedStart);
    setEndDate(selectedEnd);
    setAppliedComparison(comparison);
    setApplied({
      start_date: selectedStart.format("YYYY-MM-DD"),
      end_date: selectedEnd.format("YYYY-MM-DD"),
      territory_id: territory?.value,
      ...(comparison === "previous_period"
        ? previousPeriodComparison(selectedStart, selectedEnd)
        : { comparison }),
      ...(comparison === "custom"
        ? {
            comparison_start_date: comparisonStartDate.format("YYYY-MM-DD"),
            comparison_end_date: comparisonEndDate.format("YYYY-MM-DD"),
          }
        : {}),
    });
  }

  if (!isStaff) {
    return (
      <div className="sales-analytics-page">
        <div className="sales-analytics-access">
          <h1>Sales analytics</h1>
          <p>Only admins can view sales analytics.</p>
        </div>
      </div>
    );
  }

  const summary = report?.summary;
  const breakdowns = report?.breakdowns || {};
  const insights = report?.insights || {};
  const hasComparison = Boolean(report?.comparison);
  const currentRows = selectedDimension
    ? breakdowns[selectedDimension.key] || []
    : [];
  const decline = insights.largest_decline;
  const decliningCustomers = insights.declining_customers || [];
  const draftRange =
    startDate?.isValid() && endDate?.isValid()
      ? {
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        }
      : null;

  return (
    <div className="sales-analytics-page">
      <div className="sales-analytics-page-heading">
        <div>
          <span className="sales-analytics-eyebrow">Accounting / Reports</span>
          <h1>Sales analytics</h1>
          <p>See how your sales are performing, and where they are changing.</p>
        </div>
        {report && (
          <span className="sales-analytics-period-pill">
            {rangeLabel(report.date_range)}
          </span>
        )}
      </div>

      <form className="sales-analytics-filters" onSubmit={applyFilters}>
        <div className="sales-analytics-filter-field">
          <TextField
            select
            fullWidth
            size="small"
            label="Date range"
            value={rangePreset}
            onChange={(event) => selectRangePreset(event.target.value)}
          >
            {DATE_RANGES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div className="sales-analytics-range-preview" aria-live="polite">
          <span>Selected dates · New York time</span>
          <strong>
            {draftRange ? rangeLabel(draftRange) : "Choose dates"}
          </strong>
        </div>
        <div className="sales-analytics-filter-field">
          <TerritorySelect
            label="Territory"
            value={territory}
            onChange={(_event, value) => setTerritory(value)}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            placeholder="All territories"
          />
        </div>
        <div className="sales-analytics-filter-field">
          <TextField
            select
            fullWidth
            size="small"
            label="Compare to"
            value={comparison}
            onChange={(event) => setComparison(event.target.value)}
          >
            {COMPARISONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <Button
          className="sales-analytics-apply"
          type="submit"
          variant="contained"
          disabled={loading}
        >
          Apply
        </Button>
        {rangePreset === "custom" && (
          <div className="sales-analytics-custom-dates">
            <div className="sales-analytics-filter-field">
              <DatePicker2
                label="Start date"
                controlledValue={startDate}
                setControlledValue={setStartDate}
                size="small"
              />
            </div>
            <div className="sales-analytics-filter-field">
              <DatePicker2
                label="End date"
                controlledValue={endDate}
                setControlledValue={setEndDate}
                size="small"
              />
            </div>
          </div>
        )}
        {comparison === "custom" && (
          <div className="sales-analytics-custom-dates">
            <div className="sales-analytics-filter-field">
              <DatePicker2
                label="Comparison start"
                controlledValue={comparisonStartDate}
                setControlledValue={setComparisonStartDate}
                size="small"
              />
            </div>
            <div className="sales-analytics-filter-field">
              <DatePicker2
                label="Comparison end"
                controlledValue={comparisonEndDate}
                setControlledValue={setComparisonEndDate}
                size="small"
              />
            </div>
          </div>
        )}
        {validationError && (
          <Alert severity="error" className="sales-analytics-filter-alert">
            {validationError}
          </Alert>
        )}
      </form>

      {requestError && (
        <Alert
          severity="error"
          className="sales-analytics-request-alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setApplied({ ...applied })}
            >
              Retry
            </Button>
          }
        >
          {requestError}
        </Alert>
      )}
      {loading && !report && (
        <div className="sales-analytics-loading">
          <CircularProgress size={28} />
          <span>Loading sales analytics…</span>
        </div>
      )}

      {report && (
        <div
          className={
            loading
              ? "sales-analytics-content sales-analytics-content--loading"
              : "sales-analytics-content"
          }
        >
          <section
            aria-label="Sales summary"
            className="sales-analytics-summary"
          >
            <div className="sales-analytics-section-head sales-analytics-summary-head">
              <div>
                <span className="sales-analytics-eyebrow">The overview</span>
                <h2>Sales at a glance</h2>
              </div>
              {hasComparison && (
                <span className="sales-analytics-comparison-note">
                  Compared with {comparisonLabel(appliedComparison)} ·{" "}
                  {rangeLabel(report.comparison)}
                </span>
              )}
            </div>
            <div className="sales-analytics-metrics">
              <MetricCard
                featured
                label="Total sales"
                value={money(summary?.total_sales)}
                change={
                  hasComparison ? summary?.sales_change_percent : undefined
                }
              />
              <MetricCard
                label="Orders"
                value={countFormat.format(summary?.total_orders || 0)}
                change={
                  hasComparison
                    ? summary?.total_orders_change_percent
                    : undefined
                }
              />
              <MetricCard
                label="Customers"
                value={countFormat.format(summary?.total_customers || 0)}
                change={
                  hasComparison
                    ? summary?.total_customers_change_percent
                    : undefined
                }
              />
              <MetricCard
                label="Active accounts"
                value={countFormat.format(summary?.active_accounts || 0)}
                change={
                  hasComparison
                    ? summary?.active_accounts_change_percent
                    : undefined
                }
              />
              <MetricCard
                label="Average order size"
                value={money(summary?.average_order_size)}
                change={
                  hasComparison
                    ? summary?.average_order_size_change_percent
                    : undefined
                }
              />
            </div>
          </section>

          <SalesCharts report={report} />

          <section
            className="sales-analytics-breakdowns"
            aria-label="Sales breakdowns"
          >
            <div className="sales-analytics-section-head">
              <div>
                <span className="sales-analytics-eyebrow">
                  Find the pattern
                </span>
                <h2>Break down your sales</h2>
              </div>
              <span className="sales-analytics-section-aside">
                Select a category to see every entry
              </span>
            </div>
            <div className="sales-analytics-breakdown-grid">
              {DIMENSIONS.map((dimension) => (
                <BreakdownCard
                  key={dimension.key}
                  dimension={dimension}
                  rows={breakdowns[dimension.key] || []}
                  onOpen={() => setSelectedDimension(dimension)}
                  loading={loading}
                />
              ))}
            </div>
          </section>

          <section
            className="sales-analytics-insights"
            aria-label="Sales insights"
          >
            <div className="sales-analytics-section-head">
              <div>
                <span className="sales-analytics-eyebrow">What stands out</span>
                <h2>Insights</h2>
              </div>
            </div>
            <div className="sales-analytics-insights-grid">
              <InsightCard
                eyebrow="Top route"
                title={insights.top_route?.name}
                value={insights.top_route && money(insights.top_route.sales)}
                detail={
                  insights.top_route &&
                  `${share(insights.top_route.share_percent)} of total sales`
                }
              />
              <InsightCard
                eyebrow="Top customer"
                title={insights.top_customer?.name}
                value={
                  insights.top_customer && money(insights.top_customer.sales)
                }
                detail={
                  insights.top_customer &&
                  `${share(insights.top_customer.share_percent)} of total sales`
                }
              />
              <InsightCard
                eyebrow="Fastest growing category"
                title={insights.fastest_growing_category?.name}
                value={
                  insights.fastest_growing_category && (
                    <PercentChange
                      value={insights.fastest_growing_category.change_percent}
                      compact
                    />
                  )
                }
                detail={
                  insights.fastest_growing_category
                    ? "vs. comparison period"
                    : hasComparison
                      ? "No growth recorded"
                      : "Choose a comparison to see growth"
                }
                tone="growth"
              />
              <InsightCard
                eyebrow="Largest decline"
                title={decline?.name}
                value={decline && money(decline.change_amount)}
                detail={
                  decline?.dimension
                    ? `In ${String(decline.dimension).replaceAll("_", " ")}`
                    : decline
                      ? "Across all breakdowns"
                      : hasComparison
                        ? "No decline recorded"
                        : "Choose a comparison to see declines"
                }
                tone="decline"
              />
              <InsightCard
                eyebrow="Declining customers"
                title={
                  hasComparison
                    ? `${decliningCustomers.length} ${decliningCustomers.length === 1 ? "customer" : "customers"}`
                    : "No comparison"
                }
                detail={
                  hasComparison
                    ? decliningCustomers.length
                      ? "See the list below"
                      : "No customers declined"
                    : "Choose a comparison to see changes"
                }
              />
            </div>
            {hasComparison && decliningCustomers.length > 0 && (
              <div className="sales-analytics-declining-list">
                <div className="sales-analytics-declining-title">
                  Customers with falling sales
                </div>
                <div className="sales-analytics-declining-grid">
                  {decliningCustomers.map((customer, index) => (
                    <div
                      className="sales-analytics-declining-row"
                      key={
                        customer.key ??
                        `${customer.id ?? customer.name}-${index}`
                      }
                    >
                      <span>{customer.name || "Unassigned"}</span>
                      <strong>{money(customer.change_amount)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      <BreakdownDrawer
        key={selectedDimension?.key || "closed"}
        dimension={selectedDimension}
        rows={currentRows}
        report={report}
        onClose={() => setSelectedDimension(null)}
      />
    </div>
  );
}

export default SalesAnalytics;
